import React, { useEffect, useState } from 'react';
import { UserRound, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import DrawerShell from '@/components/DrawerShell';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { tenantAdminApi } from '@/utils/tenantAdminApi';
import { IITH } from '@/pages/landing/iithBrand';
import ProfilePhotoField from './ProfilePhotoField';
import ProfilePrivacyBooking from './ProfilePrivacyBooking';
import DoctorProfileFields from './DoctorProfileFields';
import DesignationListEditor from './DesignationListEditor';
import ProfileSection from './ProfileSection';
import { emptyProfileForm, toProfilePayload } from '../utils/userProfileForm';

function Field({ id, label, children }) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id} className="text-xs text-zinc-600">{label}</Label>
      {children}
    </div>
  );
}

/** Comprehensive staff/doctor profile editor (not used for patients). */
export default function UserProfileDrawer({ user, specs = [], open, onClose, onSaved }) {
  const [form, setForm] = useState(() => emptyProfileForm(null));
  const [saving, setSaving] = useState(false);
  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  useEffect(() => {
    if (open && user) setForm(emptyProfileForm(user));
  }, [open, user]);

  if (!open || !user) return null;

  const save = async () => {
    setSaving(true);
    try {
      const updated = await tenantAdminApi.updateUser(user.id, toProfilePayload(form));
      toast.success('Profile saved');
      onSaved?.(updated);
      onClose?.();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Could not save profile');
    } finally {
      setSaving(false);
    }
  };

  return (
    <DrawerShell
      open={open}
      onClose={onClose}
      eyebrow="Staff profile"
      title={user.name || user.email}
      subtitle="Identity · designations · clinical · portal"
      accent={IITH.saffron}
      icon={<UserRound className="h-5 w-5" />}
      width="xl"
      testId="user-profile-drawer"
      footer={(
        <>
          <Button variant="ghost" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={save} disabled={saving} style={{ background: IITH.saffron }} className="text-white"
            data-testid="profile-save">
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
            Save profile
          </Button>
        </>
      )}
    >
      <div className="px-6 py-5 space-y-4 overflow-y-auto">
        <ProfileSection title="Identity" hint="Photo and basic contact details" testId="section-identity">
          <ProfilePhotoField photoUrl={form.photo_url} onChange={(photo_url) => set({ photo_url })} />
          <div className="grid sm:grid-cols-2 gap-4">
            <Field id="pf-name" label="Full name">
              <Input id="pf-name" value={form.name} onChange={(e) => set({ name: e.target.value })} />
            </Field>
            <Field id="pf-title" label="Title">
              <Input id="pf-title" placeholder="Prof. / Dr. / …" value={form.title}
                onChange={(e) => set({ title: e.target.value })} />
            </Field>
            <Field id="pf-phone" label="Phone">
              <Input id="pf-phone" value={form.phone} onChange={(e) => set({ phone: e.target.value })} />
            </Field>
            <Field id="pf-dept" label="Department">
              <Input id="pf-dept" value={form.department}
                onChange={(e) => set({ department: e.target.value })} />
            </Field>
            <Field id="pf-cat" label="Team / category">
              <Input id="pf-cat" placeholder="Doctors / Visiting consultants…" value={form.category}
                onChange={(e) => set({ category: e.target.value })} />
            </Field>
          </div>
        </ProfileSection>

        <ProfileSection
          title="Designations"
          hint="Add current and past roles. Multiple entries supported."
          testId="section-designations"
        >
          <DesignationListEditor
            designations={form.designations}
            onChange={(designations) => set({ designations })}
          />
        </ProfileSection>

        <ProfileSection title="Clinical credentials" hint="Specialisation, registration, OPD" testId="section-clinical">
          <DoctorProfileFields form={form} set={set} specs={specs} />
        </ProfileSection>

        <ProfileSection title="Biography" testId="section-bio">
          <textarea
            id="pf-bio"
            rows={4}
            className="w-full border rounded-md px-3 py-2 text-sm"
            value={form.bio}
            onChange={(e) => set({ bio: e.target.value })}
            placeholder="Professional summary…"
          />
        </ProfileSection>

        <ProfileSection title="Portal & booking" testId="section-portal">
          <ProfilePrivacyBooking
            privacy={form.privacy}
            booking={form.booking}
            onPrivacy={(privacy) => set({ privacy })}
            onBooking={(booking) => set({ booking })}
          />
        </ProfileSection>
      </div>
    </DrawerShell>
  );
}
